import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, describeApiError } from "@/lib/api";
import { openBillingPortal } from "@/lib/billing";
import { useAuth } from "@/context/AuthContext";
import { CircleNotch, CreditCard, CalendarCheck, SealCheck, UserCircle, WarningCircle } from "@phosphor-icons/react";
import { CategorizationRulesPanel } from "@/components/CategorizationRulesPanel";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";

const money = (n, currency = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(Number(n) || 0);
const STATUS = {
  active: { label: "Active", bg: "var(--bk-success-bg)", color: "var(--bk-success)" },
  past_due: { label: "Past Due", bg: "#FDECE8", color: "#B23B1F" },
  canceled: { label: "Canceled", bg: "#F3F0EC", color: "var(--bk-text-2)" },
};

export default function Settings() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadNonce, setReloadNonce] = useState(0);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    api.get("/businesses").then((r) => {
      if (!cancelled) setCanManageBilling((r.data || []).some((business) => business.access_role === "owner"));
    }).catch(() => {});
    api.get("/subscription").then((r) => {
      if (!cancelled) setSubscription(r.data);
    }).catch((err) => {
      // Shown inline on the plan card (no toast) so a hiccup never stacks notifications.
      if (!cancelled && err.response?.status !== 403) setLoadError(describeApiError(err, "Couldn't load your plan details."));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [reloadNonce]);

  const manageBilling = async () => {
    setOpening(true);
    try {
      await openBillingPortal({ returnPath: "/settings" });
    } catch (err) {
      toast.error(err.message || "Couldn't open billing.");
      setOpening(false);
    }
  };

  const status = STATUS[subscription?.billing_status] || STATUS.active;

  return (
    <div className="min-h-screen bg-[var(--bk-page)]">
      <Header />
      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12" data-testid="settings-page">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Settings</span>
        <h1 className="font-display text-4xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>User Settings</h1>
        <p className="text-sm mt-3 max-w-2xl" style={{ color: "var(--bk-text-2)" }}>Manage your account details, billing, and the subscription Rowan is watching on your behalf.</p>

        <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6 mt-8">
          <section className="bk-card p-6" data-testid="settings-account-card">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }}>
                <UserCircle size={26} weight="duotone" />
              </span>
              <div>
                <h2 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>{user?.name || "IronRoot user"}</h2>
                <p className="text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="settings-account-email">{user?.email || "—"}</p>
              </div>
            </div>
            <div className="mt-6 rounded-2xl p-4" style={{ background: "#fff" }}>
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Current workspace mode</p>
              <p className="font-semibold mt-2" style={{ color: "var(--bk-text)" }} data-testid="settings-account-role">{user?.role === "admin" ? "Admin" : "Member"}</p>
            </div>
          </section>

          <section className="bk-card p-6 space-y-5" data-testid="settings-billing-section">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Billing & Subscription</p>
                <h2 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>Manage your plan</h2>
              </div>
              <button onClick={manageBilling} disabled={opening || loading || !canManageBilling} data-testid="settings-manage-billing-button" className="bk-btn-primary px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
                {opening ? <span className="inline-flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Opening…</span> : "Manage Billing →"}
              </button>
            </div>

            {loading ? (
              <div className="py-12 text-sm flex items-center justify-center gap-2" style={{ color: "var(--bk-text-2)" }} data-testid="settings-billing-loading">
                <CircleNotch size={18} className="animate-spin" /> Loading billing details…
              </div>
            ) : loadError ? (
              <div className="rounded-2xl border p-5 text-sm flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "rgba(214,90,49,0.35)", background: "#FDF3EF", color: "var(--bk-text)" }} role="alert" data-testid="settings-billing-error">
                <span className="inline-flex items-center gap-2"><WarningCircle size={18} weight="fill" color="var(--bk-energy)" /> {loadError}</span>
                <button type="button" onClick={() => setReloadNonce((n) => n + 1)} data-testid="settings-billing-retry" className="px-4 py-2 rounded-full text-sm font-semibold" style={{ border: "1px solid var(--bk-border)", background: "#fff", color: "var(--bk-text)" }}>Try again</button>
              </div>
            ) : !canManageBilling ? (
              <div className="rounded-2xl border p-5 text-sm" style={{ borderColor: "var(--bk-border)", background: "#fff", color: "var(--bk-text-2)" }} data-testid="settings-billing-owner-only-note">
                Billing stays with the business owner. If you're here as a partner, accountant, or employee, you've got the workspace — not the credit card drawer.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="rounded-2xl p-4" style={{ background: "#fff" }} data-testid="settings-plan-name">
                    <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Current plan</p>
                    <p className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{subscription?.plan_name || subscription?.name || "Starter"}</p>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: "#fff" }} data-testid="settings-billing-amount">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>
                      <CreditCard size={14} weight="duotone" /> Billing amount
                    </div>
                    <p className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{money(subscription?.billing_amount || 0, subscription?.billing_currency || "USD")}</p>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: "#fff" }} data-testid="settings-next-billing-date">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>
                      <CalendarCheck size={14} weight="duotone" /> {subscription?.billing_status === "canceled" ? "Access through" : "Next billing date"}
                    </div>
                    <p className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{subscription?.next_billing_date || "—"}</p>
                  </div>
                </div>

                <div className="rounded-2xl border p-5" style={{ borderColor: "var(--bk-border)", background: "#fff" }}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Subscription status</p>
                      <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold" style={{ background: status.bg, color: status.color }} data-testid="settings-status-badge">
                        <SealCheck size={15} weight="duotone" /> {status.label}
                      </div>
                    </div>
                    <p className="text-sm max-w-xl text-right" style={{ color: "var(--bk-text-2)" }} data-testid="settings-rowan-message">
                      {subscription?.rowan_message || "Your subscription is healthy. Rowan approves."}
                    </p>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        <ChangePasswordCard />

        <CategorizationRulesPanel />
      </main>
    </div>
  );
}